// src/components/documentation/DocumentationLayout.jsx

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook,
    faChevronRight,
    faChevronDown,
    faBars,
    faTimes,
    faQrcode,
    faCog,
    faUsers,
    faChartBar,
    faBullhorn,
    faGift,
    faStore,
    faEnvelope,
    faMobileAlt,
    faRocket,
    faUser,
    faSignInAlt,
    faTags,
    faBox,
    faUserClock,
    faImage,
    faChartLine,
    faFileAlt,
    faPoll,
    faBell,
    faUserTie,
    faCrown
} from '@fortawesome/free-solid-svg-icons';

const DocumentationLayout = ({ children }) => {
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    
    // Estado para controlar qué categorías están expandidas
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // Determinar qué categoría contiene la página actual
        const currentPath = location.pathname;
        const categoryMap = {
            '/documentation': 'Inicio',
            '/documentation/welcome': 'Inicio',
            '/documentation/quick-start': 'Quick Start',
            '/documentation/gestion': 'Gestión',
            '/documentation/marketing': 'Marketing',
            '/documentation/analytics': 'Analytics',
            '/documentation/feedback': 'Feedback',
            '/documentation/usuarios': 'Usuarios',
            '/documentation/integraciones': 'Integraciones'
        };
        
        // Encontrar la categoría actual
        let currentCategory = 'Inicio';
        for (const [path, category] of Object.entries(categoryMap)) {
            if (currentPath.startsWith(path)) {
                currentCategory = category;
                break;
            }
        }
        
        // Expandir la categoría actual y algunas principales por defecto
        return {
            'Inicio': true,
            'Quick Start': currentCategory === 'Quick Start',
            'Gestión': currentCategory === 'Gestión',
            'Marketing': currentCategory === 'Marketing',
            'Analytics': currentCategory === 'Analytics',
            'Feedback': currentCategory === 'Feedback',
            'Usuarios': currentCategory === 'Usuarios',
            'Integraciones': currentCategory === 'Integraciones',
            'Configuración': false
        };
    });
    
    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const menuItems = [
        {
            id: 'index',
            title: 'Inicio',
            icon: faBook,
            path: '/documentation',
            description: 'Centro de ayuda'
        },
        {
            id: 'welcome',
            title: 'Bienvenida',
            icon: faStore,
            path: '/documentation/welcome',
            description: 'Presentación de PetitPrime',
            category: 'Inicio'
        },
        {
            id: 'quick-start-index',
            title: 'Inicio Rápido',
            icon: faRocket,
            path: '/documentation/quick-start',
            description: 'Guía paso a paso',
            category: 'Quick Start'
        },
        {
            id: 'quick-start-registro',
            title: 'Registro',
            icon: faUser,
            path: '/documentation/quick-start/registro',
            description: 'Crear tu cuenta',
            category: 'Quick Start'
        },
        {
            id: 'quick-start-login',
            title: 'Login',
            icon: faSignInAlt,
            path: '/documentation/quick-start/login',
            description: 'Iniciar sesión',
            category: 'Quick Start'
        },
        {
            id: 'quick-start-onboarding',
            title: 'Onboarding',
            icon: faRocket,
            path: '/documentation/quick-start/onboarding',
            description: 'Configurar tu negocio',
            category: 'Quick Start'
        },
        {
            id: 'gestion-index',
            title: 'Gestión',
            icon: faCog,
            path: '/documentation/gestion',
            description: 'Administra tu negocio',
            category: 'Gestión'
        },
        {
            id: 'gestion-marcas-tiendas',
            title: 'Marcas y Tiendas',
            icon: faTags,
            path: '/documentation/gestion/marcas-tiendas',
            description: 'Gestionar marcas y tiendas',
            category: 'Gestión'
        },
        {
            id: 'gestion-clientes',
            title: 'Clientes',
            icon: faUsers,
            path: '/documentation/gestion/clientes',
            description: 'Gestionar clientes y puntos',
            category: 'Gestión'
        },
        {
            id: 'gestion-productos',
            title: 'Productos',
            icon: faBox,
            path: '/documentation/gestion/productos',
            description: 'Gestionar catálogo e inventario',
            category: 'Gestión'
        },
        {
            id: 'gestion-ventas',
            title: 'Ventas',
            icon: faChartBar,
            path: '/documentation/gestion/ventas',
            description: 'Importar y analizar ventas',
            category: 'Gestión'
        },
        {
            id: 'marketing-index',
            title: 'Marketing',
            icon: faBullhorn,
            path: '/documentation/marketing',
            description: 'Herramientas de marketing',
            category: 'Marketing'
        },
        {
            id: 'marketing-ofertas',
            title: 'Ofertas',
            icon: faTags,
            path: '/documentation/marketing/ofertas',
            description: 'Gestionar ofertas promocionales',
            category: 'Marketing'
        },
        {
            id: 'marketing-winback',
            title: 'Recuperar Clientes Perdidos',
            icon: faUserClock,
            path: '/documentation/marketing/winback',
            description: 'Sistema inteligente de WinBack',
            category: 'Marketing'
        },
        {
            id: 'marketing-recompensas',
            title: 'Recompensas',
            icon: faGift,
            path: '/documentation/marketing/recompensas',
            description: 'Gestionar premios y descuentos',
            category: 'Marketing'
        },
        {
            id: 'marketing-email',
            title: 'Email Marketing',
            icon: faEnvelope,
            path: '/documentation/marketing/email-marketing',
            description: 'Campañas de email personalizadas',
            category: 'Marketing'
        },
        {
            id: 'marketing-infografias',
            title: 'Generar Infografías',
            icon: faImage,
            path: '/documentation/marketing/infografias',
            description: 'Editor visual de infografías',
            category: 'Marketing'
        },
        {
            id: 'analytics-index',
            title: 'Analytics',
            icon: faChartLine,
            path: '/documentation/analytics',
            description: 'Analytics y reportes',
            category: 'Analytics'
        },
        {
            id: 'analytics-dashboard',
            title: 'Dashboard Analytics',
            icon: faChartLine,
            path: '/documentation/analytics/dashboard',
            description: 'Dashboard analítico con KPIs',
            category: 'Analytics'
        },
        {
            id: 'analytics-clientes',
            title: 'Estadísticas de Clientes',
            icon: faUsers,
            path: '/documentation/analytics/clientes',
            description: 'Análisis de clientes',
            category: 'Analytics'
        },
        {
            id: 'analytics-tiendas',
            title: 'Estadísticas de Tiendas',
            icon: faStore,
            path: '/documentation/analytics/tiendas',
            description: 'Métricas por tienda',
            category: 'Analytics'
        },
        {
            id: 'analytics-ofertas',
            title: 'Métricas de Ofertas',
            icon: faTags,
            path: '/documentation/analytics/ofertas',
            description: 'Rendimiento de ofertas',
            category: 'Analytics'
        },
        {
            id: 'analytics-reporting',
            title: 'Centro de Reporting',
            icon: faFileAlt,
            path: '/documentation/analytics/reporting',
            description: 'Generar reportes ejecutivos',
            category: 'Analytics'
        },
        {
            id: 'feedback-index',
            title: 'Feedback y Alertas',
            icon: faPoll,
            path: '/documentation/feedback',
            description: 'Encuestas y alertas',
            category: 'Feedback'
        },
        {
            id: 'feedback-encuestas',
            title: 'Encuestas',
            icon: faPoll,
            path: '/documentation/feedback/encuestas',
            description: 'Crear y gestionar encuestas',
            category: 'Feedback'
        },
        {
            id: 'feedback-alertas',
            title: 'Alertas',
            icon: faBell,
            path: '/documentation/feedback/alertas',
            description: 'Centro de alertas',
            category: 'Feedback'
        },
        {
            id: 'usuarios-index',
            title: 'Tipos de Usuarios',
            icon: faUser,
            path: '/documentation/usuarios',
            description: 'Dueño, Trabajador y Franquiciado',
            category: 'Usuarios'
        },
        {
            id: 'usuarios-dueño',
            title: 'Dueño de la Marca',
            icon: faCrown,
            path: '/documentation/usuarios/dueño',
            description: 'Usuario principal con acceso completo',
            category: 'Usuarios'
        },
        {
            id: 'usuarios-trabajador',
            title: 'Trabajador',
            icon: faUserTie,
            path: '/documentation/usuarios/trabajador',
            description: 'Personal de tienda con acceso limitado',
            category: 'Usuarios'
        },
        {
            id: 'usuarios-franquiciado',
            title: 'Franquiciado',
            icon: faStore,
            path: '/documentation/usuarios/franquiciado',
            description: 'Dueño de franquicia con estadísticas',
            category: 'Usuarios'
        },
        {
            id: 'tpv',
            title: 'TPV Agora',
            icon: faQrcode,
            path: '/documentation/integraciones/tpv-agora',
            description: 'Configurar TPV Agora',
            category: 'Integraciones'
        },
        {
            id: 'campanas',
            title: 'Campañas',
            icon: faBullhorn,
            path: '/documentation/campanas',
            description: 'Crear y gestionar campañas',
            category: 'Marketing',
            comingSoon: true
        },
        {
            id: 'recompensas',
            title: 'Recompensas',
            icon: faGift,
            path: '/documentation/recompensas',
            description: 'Configurar recompensas y premios',
            category: 'Marketing',
            comingSoon: true
        },
        {
            id: 'comunicacion',
            title: 'Comunicación',
            icon: faEnvelope,
            path: '/documentation/comunicacion',
            description: 'Enviar mensajes y notificaciones',
            category: 'Marketing',
            comingSoon: true
        }
    ];

    const categories = [
        { id: 'Inicio', items: menuItems.filter(item => item.category === 'Inicio' || !item.category) },
        { id: 'Quick Start', items: menuItems.filter(item => item.category === 'Quick Start') },
        { id: 'Gestión', items: menuItems.filter(item => item.category === 'Gestión' && !item.comingSoon) },
        { id: 'Marketing', items: menuItems.filter(item => item.category === 'Marketing' && !item.comingSoon) },
        { id: 'Analytics', items: menuItems.filter(item => item.category === 'Analytics' && !item.comingSoon) },
        { id: 'Feedback', items: menuItems.filter(item => item.category === 'Feedback' && !item.comingSoon) },
        { id: 'Usuarios', items: menuItems.filter(item => item.category === 'Usuarios' && !item.comingSoon) },
        { id: 'Integraciones', items: menuItems.filter(item => item.category === 'Integraciones') },
        { id: 'Configuración', items: menuItems.filter(item => item.category === 'Configuración') }
    ].filter(cat => cat.items && cat.items.length > 0);

    const isActive = (path) => {
        if (path === '/documentation') {
            return location.pathname === '/documentation';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />
                            </button>
                            <Link to="/documentation" className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <FontAwesomeIcon icon={faBook} className="text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold text-gray-800">Centro de Ayuda</h1>
                                    <p className="text-xs text-gray-500">Guías y documentación</p>
                                </div>
                            </Link>
                        </div>
                        <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
                            <Link to="/" className="hover:text-gray-700">Inicio</Link>
                            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                            <span className="text-gray-700">Documentación</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex gap-8">
                    {/* Sidebar */}
                    <aside
                        className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                            } fixed lg:sticky lg:translate-x-0 top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-white border-r shadow-sm overflow-y-auto z-30 transition-transform duration-300 lg:block`}
                    >
                        <div className="p-6">
                            <nav className="space-y-4">
                                {categories.map((category) => {
                                    const isExpanded = expandedCategories[category.id] ?? true;
                                    const hasActiveItem = category.items.some(item => isActive(item.path));
                                    
                                    return (
                                        <div key={category.id} className="border-b border-gray-100 pb-4 last:border-b-0">
                                            <button
                                                onClick={() => toggleCategory(category.id)}
                                                className={`w-full flex items-center justify-between text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 hover:text-gray-900 transition-colors ${
                                                    hasActiveItem ? 'text-blue-600' : ''
                                                }`}
                                            >
                                                <span>{category.id}</span>
                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                    className={`text-xs transition-transform ${hasActiveItem ? 'text-blue-600' : 'text-gray-400'}`}
                                                />
                                            </button>
                                            {isExpanded && (
                                                <ul className="space-y-1">
                                                    {category.items.map((item) => (
                                                <li key={item.id}>
                                                    {item.comingSoon ? (
                                                        <div className="flex items-center gap-3 px-3 py-2 text-gray-400 cursor-not-allowed">
                                                            <FontAwesomeIcon icon={item.icon} className="text-sm" />
                                                            <div className="flex-1">
                                                                <div className="text-sm font-medium">{item.title}</div>
                                                                <div className="text-xs text-gray-400">{item.description}</div>
                                                            </div>
                                                            <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded">
                                                                Próximamente
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <Link
                                                            to={item.path}
                                                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive(item.path)
                                                                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                                                                    : 'text-gray-700 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={item.icon}
                                                                className={`text-sm ${isActive(item.path) ? 'text-blue-600' : 'text-gray-400'}`}
                                                            />
                                                            <div className="flex-1">
                                                                <div className="text-sm font-medium">{item.title}</div>
                                                                <div className="text-xs text-gray-500">{item.description}</div>
                                                            </div>
                                                            {isActive(item.path) && (
                                                                <FontAwesomeIcon icon={faChevronRight} className="text-xs text-blue-600" />
                                                            )}
                                                        </Link>
                                                    )}
                                                </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    {/* Overlay para móvil */}
                    {sidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
                            onClick={() => setSidebarOpen(false)}
                        />
                    )}

                    {/* Main Content */}
                    <main className="flex-1 min-w-0">
                        <div className="bg-white rounded-lg shadow-sm border">
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default DocumentationLayout;

